import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Grid,
  Avatar,
  IconButton,
} from "@mui/material";
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  CameraAlt as CameraAltIcon,
  Attachment,
} from "@mui/icons-material";
import vendorService from "@/modules/vendor/service/vendor.int.service";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setAuthInfo } from "@/redux/auth/auth.slice";
import ToastService from "@/service/toaster.service";
import { isBusinessEmail } from "@/helpers/email.helper";
import ExternalLinksSection from "./components/ExternalLinksSection";
import ServicesProvidedSection from "./components/ServiceProvided";
import DescriptionSection from "./components/DescriptionSection";
import PreviousWorkSection from "./components/PreviousWorkSection";
import PersonalInformationCard from "./components/PersonalInformationCard";
import AttachmentSection from "./components/AttachmentSection";



const PersonalDetails = () => {
  const [isEditModePersonalInformation, setIsEditModePersonalInformation] = useState(false);
  const [formData, setFormData] = useState();
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [image, setImage] = useState({ file_url: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);

  const [isEditingDescription, setIsEditing] = useState(false);
  const [isEditingServices, setIsEditingServices] = useState(false);
  const [isEditingExternalLinks, setIsEditingExternalLinks] = useState(false);
  const [errors, setErrors] = useState({});

  const user = useSelector((state) => state.auth.auth);
  const dispatch = useDispatch();

  const isAnyButtonInEditMode = isEditModePersonalInformation || isEditingDescription || isEditingServices || isEditingExternalLinks;


  const vendorTypes = [
    "Realestate Agent",
    "Conveyancer/Solicitor",
    "Building & Pest inspector",
    "Buyers agent",
    "Mortgage Broker",
    "Property Manager",
    "Accountant",
    "Building Surveyor"
  ];

  const fetchVendorData = async () => {
    if (user) {
      try {
        const response = await vendorService.getVendorById();
        setFormData(response.data.data);
        setImage(response.data.data.profile_picture);
        setExperiences(response.data.data.work_experience);
      } catch (error) {
        console.error("Error fetching vendor data:", error);
        ToastService.error("Error fetching vendor data");
      }
    } else {
      ToastService.error("User not found");
    }
  };

  useEffect(() => {
    fetchVendorData();
  }, [user]);

  const handleAddExperience = () => {
    setSelectedExperience(null); 
    setIsModalOpen(true);
  };

  const handleEditExperience = (experience) => {
    setSelectedExperience(experience);
    setIsModalOpen(true);
  };

  const handleDeleteExperience = (experience) => {
    const updatedExperiences = experiences.filter(
      (exp) => exp._id !== experience._id
    );
  
    setExperiences(updatedExperiences); 
  
    const data = new FormData();
    data.append("work_experience", JSON.stringify(updatedExperiences));
  
    vendorService
      .submitProfileData(user._id, data)
      .then((response) => {
        ToastService.success("Experience deleted successfully");
        setExperiences(response.data.data.work_experience);
      })
      .catch((error) => {
        console.error("Error deleting experience:", error);
      });
  };

  const handleSaveExperience = async (newExperience) => {
    try {
      let updatedExperiences;
      if (selectedExperience) {
        updatedExperiences = experiences.map((exp) =>
          exp.job_title === selectedExperience.job_title &&
            exp.organization === selectedExperience.organization &&
            exp.starting === selectedExperience.starting
            ? newExperience
            : exp
        );
      } else {
        updatedExperiences = [...experiences, newExperience];
      }
      setExperiences(updatedExperiences);
      let data = new FormData();
      data.append("work_experience", JSON.stringify(updatedExperiences));
      const response = await vendorService.submitProfileData(user._id, data);
      ToastService.success("Work Experience updated");
      setFormData(response.data.data);
      setExperiences(response.data.data.work_experience);
    } catch (error) {
      console.error("Error updating experience:", error);
      ToastService.error("Error updating experience");
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleEditClickPersonalInfo = () => {
    setIsEditModePersonalInformation(!isEditModePersonalInformation);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;
    if (name === "first_name" || name === "last_name") {
      updatedValue = value.trim();
    }

    setFormData((prevData) => ({ ...prevData, [name]: updatedValue }));

    let updatedErrors = { ...errors };
    switch (name) {

      case "first_name":
        if (value.trim() === "") {
          updatedErrors.first_name = "First Name is required.";
        } else {
          delete updatedErrors.first_name;
        }
        break;

      case "last_name":
        if (value.trim() === "") {
          updatedErrors.last_name = "Last Name is required.";
        } else {
          delete updatedErrors.last_name;
        }
        break;

      case "full_name":
        if (value.trim() === "") {
          updatedErrors.full_name = "Full Name is required.";
        } else {
          delete updatedErrors.full_name;
        }
        break;

      case "email":
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
          updatedErrors.email = "Please enter a valid email.";
        } else {
          delete updatedErrors.email;
        }
        break;

      case "business_name":
        if (value.trim() === "") {
          updatedErrors.business_name = "Business name is required.";
        } else {
          delete updatedErrors.business_name;
        }
        break;

      case "professional_title":
        if (value.trim() === "") {
          updatedErrors.professional_title = "Professional Title is required.";
        } else {
          delete updatedErrors.professional_title;
        }
        break;


      case "phone_number":
        if (value.trim() !== "") {
          const phoneRegex = /^[0-9]{10,11}$/;
          if (!phoneRegex.test(value)) {
            updatedErrors.phone_number = "Phone number must be number and between 8 to 10 digits.";
          } else {
            delete updatedErrors.phone_number;
          }
        }
        break;

      case "vendor_type":
        if (value === "") {
          updatedErrors.vendor_type = "Please select a vendor type.";
        } else {
          delete updatedErrors.vendor_type;
        }
        break;

      case "description":
        if (value === "") {
          updatedErrors.description = "Description cannot be empty";
        } else if (value.length > 500) {
          updatedErrors.description = "Description cannot exceed 500 characters";
        } else {
          delete updatedErrors.description;
        }
        break;

      case "website":
        // Allow empty, or check if the value is a valid URL with or without https://
        if (value !== "" && !/^(https?:\/\/)?([\w\-]+\.)+[a-z]{2,6}(\/[\w\-]*)*$/i.test(value)) {
          updatedErrors.website = "Please enter a valid website URL";
        } else {
          delete updatedErrors.website; // No error if value is empty or valid
        }
        break;


      case "work_email":
        if (value === "") {
          updatedErrors.work_email = "Work email cannot be empty";
        } else {
          const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          if (!emailPattern.test(value)) {
            updatedErrors.work_email = "Please enter a valid email address";
          } else if (!isBusinessEmail(value)) {
            updatedErrors.work_email = "Please use your business email, not a public one like Gmail or Yahoo";
          } else {
            delete updatedErrors.work_email;
          }
        }
        break;

      default:
        break;
    }
    setErrors(updatedErrors);
  };


  const handleSaveClick = async () => {
    try {
      // Prepare form data
      const data = new FormData();
      // data.append("is_approved", "true");
      data.append("first_name", formData.first_name);
      data.append("last_name", formData.last_name);
      data.append("full_name", formData.full_name);
      data.append("vendor_type", formData.vendor_type);
      data.append("business_name", formData.business_name);
      data.append("professional_title", formData.professional_title);
      data.append("phone_number", formData.phone_number);
      data.append("address", formData.address);
      data.append("description", formData.description);

      data.append("website", formData.website);
      data.append("work_email", formData.work_email);
      data.append("linkedin", formData.linkedin);
      data.append("certifications_affiliations", formData.certifications_affiliations);
      // data.append('profile_picture', formData.profile_picture);  // Assuming profile picture is already updated
      if (latitude && longitude) {
        data.append("longitude", longitude);
        data.append("latitude", latitude);
      }
      data.append("post_code", formData?.post_code || "");
      data.append("services_provided", formData.services_provided);
      data.append("mortgage_pre_approval", formData.mortgage_pre_approval);
      data.append("investment_property_loans", formData.investment_property_loans);
      data.append("home_equity_loans", formData.home_equity_loans);
      data.append("first_time_home_buyer_loans", formData.first_time_home_buyer_loans);
      data.append("debt_consolidation", formData.debt_consolidation);
      data.append("custom_loan_solutions", formData.custom_loan_solutions);
      data.append("refinancing_services", formData.refinancing_services);
      data.append("interest_rate_optimization", formData.interest_rate_optimization);
      data.append("construction_loans", formData.construction_loans);
      data.append("loan_application_assistance", formData.loan_application_assistance);
      data.append("government_backed_loans", formData.government_backed_loans);
      data.append("reverse_mortgages", formData.reverse_mortgages);
      data.append("credit_score_consultation", formData.credit_score_consultation);
      // Send the request

      const response = await vendorService.submitProfileData(user._id, data);
      // Assuming success response to exit the edit mode
      if (response) {

        dispatch(setAuthInfo({ ...response.data.data, role: "vendor", id: response.data.data._id }));

        setIsEditModePersonalInformation(false); 
        ToastService.success("Profile updated successfully");
      } else {
        console.error("Error saving data");
      }
    } catch (error) {
      console.error("Error:", error);
      ToastService.error(error.response.data.data.error);
    }
  };

  // Services list
  const serviceList = [
    { name: "Mortgage Pre-Approval", key: "mortgage_pre_approval" },
    { name: "Investment Property Loans", key: "investment_property_loans" },
    { name: "Home Equity Loans", key: "home_equity_loans" },
    { name: "First-Time Home Buyer Loans", key: "first_time_home_buyer_loans" },
    { name: "Debt Consolidation", key: "debt_consolidation" },
    { name: "Custom Loan Solutions", key: "custom_loan_solutions" },
    { name: "Refinancing Services", key: "refinancing_services" },
    { name: "Interest Rate Optimization", key: "interest_rate_optimization" },
    { name: "Construction Loans", key: "construction_loans" },
    { name: "Loan Application Assistance", key: "loan_application_assistance" },
    { name: "Government Backed Loans", key: "government_backed_loans" },
    { name: "Reverse Mortgages", key: "reverse_mortgages" },
    { name: "Credit Score Consultation", key: "credit_score_consultation" },
  ];

  // Handle checkbox change in edit mode
  const handleCheckboxChange1 = (key) => {
    setFormData((prevData) => ({
      ...prevData,
      [key]: !prevData[key],
    }));
  };


  const handleCheckboxChange = (serviceId) => {
    setFormData((prevData) => {
      const currentServices = prevData.services || [];
  
      const updatedServices = currentServices.some(
        (s) =>
          (typeof s === "string" && s === serviceId) ||
          (typeof s === "object" && s._id === serviceId)
      )
        ? currentServices.filter(
            (s) =>
              (typeof s === "string" && s !== serviceId) ||
              (typeof s === "object" && s._id !== serviceId)
          )
        : [...currentServices, serviceId];
  
      return {
        ...prevData,
        services: updatedServices,
      };
    });
  };

  // Toggle between edit and save modes
  const toggleEditModeServices = async () => {
    if (isEditingServices) {
      const validationError = validateServicesForm();
      if (validationError) {
        // alert(validationError); // or handle error message in UI
        // return; // Prevent save if validation fails
      }
      await handleSaveClick();
    }
    setIsEditingServices(!isEditingServices); // Toggle edit mode
  };

  const toggleEditModeServices1 = async () => {
    if (isEditingServices) {
      const validationError = validateServicesForm();
      if (validationError) {
        // Optionally show error in UI
        return; // Stop toggle if validation fails
      }
  
      const success = await handleSaveClick();
      if (!success) return; // Don't toggle if save failed
    }
  
    setIsEditingServices((prev) => !prev); // Only toggle when allowed
  };

  const validateServicesForm = () => {
    // Check if at least one service is selected (true)
    const isAnyServiceChecked = Object.keys(formData).some(key => formData[key] === true);

    if (!isAnyServiceChecked) {
      return "At least one service must be selected";
    }

    return null;
  };

  const toggleEditDescription = () => {
    if (isEditingDescription) {
      const saveData = async () => {
        try {
          await handleSaveClick();
        } catch (error) {
          console.error("Error saving data:", error);
        }
      };
      saveData();
    }
    setIsEditing(!isEditingDescription);
  };

  // Toggle between edit and save modes
  const toggleEditModeExternalLink = () => {
    if (isEditingExternalLinks) {
      handleSaveClick();
    }
    setIsEditingExternalLinks(!isEditingExternalLinks); // Toggle between edit and view modes
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0]; // Get the selected file
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        setImage({ file_url: reader.result }); // Set the image state with the new profile picture URL
        await uploadImageToAPI(file);
        ToastService.success("Profile picture updated successfully");

      };
      reader.readAsDataURL(file); // Convert the image to a data URL
    }
  };

  const uploadImageToAPI = async (file) => {
    try {
      const payload = new FormData();
      payload.append("profile_picture", file); // Assuming profile picture is already updated
      const response = await vendorService.submitProfileData(user._id, payload);
      dispatch(setAuthInfo({ ...response.data.data, role: "vendor", id: response.data.data._id }));
      setFormData(response.data.data);
      setExperiences(response.data.data.work_experience);
    } catch (error) {
      ToastService.warning(error.response.data.data.error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Adding 1 since months are 0-indexed
    return `${year}/${month}`;
  };

  const handleAddressSelection = (address) => {
    setFormData((prevData) => ({ ...prevData, ['address']: address.address, ["post_code"]: address.postalCode }));
    setLatitude(address.latitude || ""); // Update the latitude state
    setLongitude(address.longitude || ""); // Update the longitude state
  }
  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={3}>
        <Box
          sx={{
            pt: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
          }}
        >
          {/* Avatar */}
          <Avatar
            src={image && image.file_url}
            alt="Profile Picture"
            sx={{ boxShadow: 5, width: 140, height: 140, mb: 2 }}
          />

          {/* Update Profile Picture Icon */}
          <IconButton
            component="label"
            sx={{
              position: "absolute",
              bottom: 0,
              right: 100,
              backgroundColor: "rgba(255, 255, 255, 0.85)",
              borderRadius: "50%",
              boxShadow: 5,
              "&:hover": {
                backgroundColor: "rgba(253, 253, 253, 0.83)",
                boxShadow: 2,
              }
            }}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
            />
            <CameraAltIcon sx={{ color: "#5e5acf" }} />
          </IconButton>

        </Box>
      </Grid>
      <Grid item xs={12} md={9}>
        <Card variant="outlined" sx={{ mb: 4, p: 3 }}>
            <PersonalInformationCard
            isEditModePersonalInformation={isEditModePersonalInformation}
            isAnyButtonInEditMode={isAnyButtonInEditMode}
            errors={errors}
            formData={formData}
            handleSaveClick={handleSaveClick}
            handleEditClickPersonalInfo={handleEditClickPersonalInfo}
            handleChange={handleChange}
            vendorTypes={vendorTypes}
            handleAddressSelection={handleAddressSelection}
            setFormData={setFormData}
          />
        </Card>

        <Card variant="outlined" sx={{ mb: 4 }}> 
        <ServicesProvidedSection
              vendorTypes={vendorTypes}
              handleChange={handleChange}
              isEditingServices={isEditingServices}
              toggleEditModeServices={toggleEditModeServices}
              isAnyButtonInEditMode={isAnyButtonInEditMode}
              errors={errors}
              formData={formData}
              handleCheckboxChange={handleCheckboxChange}
              serviceList={serviceList}
              isEditModePersonalInformation={isEditModePersonalInformation}
            />
        </Card> 

        {/* <Card variant="outlined" sx={{ mb: 4 }}>  */}
        <PreviousWorkSection
            experiences={experiences}
            isEditModePersonalInformation={isEditModePersonalInformation}
            errors={errors}
            isAnyButtonInEditMode={isAnyButtonInEditMode}
            handleAddExperience={handleAddExperience}
            handleEditExperience={handleEditExperience}
            handleDeleteExperience={handleDeleteExperience}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            selectedExperience={selectedExperience}
            setSelectedExperience={setSelectedExperience}
            handleSaveExperience={handleSaveExperience}
            formatDate={formatDate}
          />
        {/* </Card>  */}

         <Card variant="outlined" sx={{ mb: 4 }}> 
            <DescriptionSection
              isEditingDescription={isEditingDescription}
              toggleEditDescription={toggleEditDescription}
              isAnyButtonInEditMode={isAnyButtonInEditMode}
              errors={errors}
              formData={formData}
              handleChange={handleChange}
              isEditModePersonalInformation={isEditModePersonalInformation}
            />
        </Card> 

        <Card variant="outlined" sx={{ mb: 4, p: 3 }}>
          
          <ExternalLinksSection
            formData={formData}
            errors={errors}
            isEditingExternalLinks={isEditingExternalLinks}
            toggleEditModeExternalLink={toggleEditModeExternalLink}
            handleChange={handleChange}
            isAnyButtonInEditMode={isAnyButtonInEditMode}
            isEditModePersonalInformation={isEditModePersonalInformation}
          />

          <AttachmentSection
            formData={formData}
            setFormData={setFormData}
          />
        </Card>
      </Grid>
    </Grid>
  );
};

export default PersonalDetails;
